import { makeStyles } from '@material-ui/core'
import PropTypes from 'prop-types'
import React from 'react'

import { formatDate, formatPrice, generateOrderNumber } from 'src/utils'
import { titleCase } from 'title-case'

import Chip from './Chip'
import Container from './Container'
import OrganisationName from './OrganisationName'
import PageTitle from './PageTitle'
import ProductList from './ProductList'
import SidePanel from './SidePanel'
import Typography from './Typography'

const useStyles = makeStyles(theme => ({
  body: {
    backgroundColor: theme.palette.fadedGreen.main,
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    flexDirection: 'column-reverse',
    padding: theme.spacing(2),
    [theme.breakpoints.up('tablet')]: {
      flexDirection: 'row',
    },
  },
  header: {
    fontFamily: 'Gotham',
    fontWeight: 500,
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(2),
    textTransform: 'uppercase',
  },
  chip: {
    marginLeft: theme.spacing(2),
  },
  content: {
    flexGrow: 1,
  },
  orderAgain: {
    marginBottom: theme.spacing(2),
  },
  productTable: {
    borderRadius: theme.shape.borderRadius,
    width: '100%',
  },
  sidePanelContainer: {
    flexShrink: 0,
    marginLeft: 0,
    marginBottom: theme.spacing(4),
    width: 'auto',
    [theme.breakpoints.up('tablet')]: {
      marginBottom: 0,
      marginLeft: theme.spacing(4),
      width: 250,
    },
  },
  notes: {
    paddingLeft: theme.spacing(0.5),
  },
}))

const Order = order => {
  const {
    addressName,
    addressLine1,
    addressLine2,
    addressCity,
    addressCounty,
    addressPostcode,
    createdAt,
    lineItems,
    notes,
    organisation,
    price,
    sidePanelItems,
    status,
    statusChangedAt,
    disputeNotes,
    showDisputed,
    scheduleLater,
  } = order
  const classes = useStyles()

  const products = lineItems.map(lineItem => ({
    available: true,
    // Agreement product will be blank if it is no longer available
    id: lineItem.agreementProduct?.product.id,
    avEquivalentUnitSize: lineItem.productAvEquivalentUnitSize,
    code: lineItem.productCode,
    excludeFromOrder: lineItem.excludeFromOrder,
    name: lineItem.productName,
    price: lineItem.productPrice,
    unit: lineItem.productUnit,
    quantity: lineItem.quantity,
    totalSizeSent: lineItem.totalSizeSent,
    updatedProductName: lineItem.updatedProductName,
    updatedProductPrice: lineItem.updatedProductPrice,
    updatedQuantity: lineItem.updatedQuantity,
    updatedProductUnit: lineItem.updatedProductUnit,
    variablePrice: lineItem.productVariablePrice,
    variablePriceUnit: lineItem.productVariablePriceUnit,
    vatRate: lineItem.productVatRate,
    disputeReason: lineItem.disputeReason,
  }))

  const orderNumber = generateOrderNumber({ ...order, supplier: organisation })

  return (
    <Container>
      <PageTitle>
        Order {orderNumber}
        <Chip
          label={titleCase(status.replace(/_/g, ' '))}
          className={classes.chip}
        />
      </PageTitle>
      <OrganisationName {...organisation} />
      <div className={classes.body}>
        <div className={classes.content}>
          <ProductList
            noCategories
            products={products}
            showDescription={false}
            showPhoto={false}
            showQuantity
            tableClassName={classes.productTable}
            showDisputed={showDisputed}
            drilledDisputeNotes={disputeNotes}
          />
        </div>
        <div className={classes.sidePanelContainer}>
          <SidePanel>
            {status === 'dispatched' ? (
              <SidePanel.Item title="Dispatched On">
                <Typography variant="body2">
                  {formatDate(statusChangedAt)}
                </Typography>
              </SidePanel.Item>
            ) : status === 'cancelled' ? (
              <SidePanel.Item title="Cancelled On">
                <Typography variant="body2">
                  {formatDate(statusChangedAt)}
                </Typography>
              </SidePanel.Item>
            ) : null}
            <SidePanel.Item title="Date Submitted">
              <Typography variant="body2">{formatDate(createdAt)}</Typography>
            </SidePanel.Item>
            <SidePanel.Item title="Total">
              <Typography variant="body2">{formatPrice(price)}</Typography>
            </SidePanel.Item>
            <SidePanel.Item title="Address">
              <Typography variant="body2">{addressName}</Typography>
              <Typography variant="body2">{addressLine1}</Typography>
              <Typography variant="body2">{addressLine2}</Typography>
              <Typography variant="body2">{addressCity}</Typography>
              <Typography variant="body2">{addressCounty}</Typography>
              <Typography variant="body2">{addressPostcode}</Typography>
            </SidePanel.Item>
            {notes ? (
              <SidePanel.Item title="Order Notes">
                <Typography variant="body2">{notes}</Typography>
              </SidePanel.Item>
            ) : null}
            {scheduleLater ? (
              <SidePanel.Item title="Requested Delivery Date">
                <Typography variant="body2">
                  {formatDate(scheduleLater)}
                </Typography>
              </SidePanel.Item>
            ) : null}
            {sidePanelItems}
          </SidePanel>
        </div>
      </div>
    </Container>
  )
}

Order.propsTypes = {
  id: PropTypes.number,
  addressName: PropTypes.string,
  addressLine1: PropTypes.string,
  addressLine2: PropTypes.string,
  addressCity: PropTypes.string,
  addressCounty: PropTypes.string,
  addressPostcode: PropTypes.string,
  organisation: PropTypes.object,
  createdAt: PropTypes.string,
  lineItems: PropTypes.array,
  price: PropTypes.string,
  status: PropTypes.oneOf(['dispatched', 'cancelled', 'submitted']),
  statusChangedAt: PropTypes.string,
  disputeNotes: PropTypes.array,
}

export default Order
